import { useState, useMemo, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PersonTypeBadge } from "@/components/people/PersonTypeBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Plus, MoreHorizontal, Edit, Trash2, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePeople, useSoftDeletePerson, PersonType } from "@/hooks/usePeople";
import { useAllSkillRatings } from "@/hooks/usePersonSkillRatings";
import { useDefaultFunctions } from "@/hooks/useDefaultFunctions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

function PersonLinkCell({
  children,
  className,
  to,
}: {
  children: ReactNode;
  className?: string;
  to: string;
}) {
  return (
    <TableCell className={cn("p-0", className)}>
      <Link to={to} className="block h-full p-4 text-inherit no-underline">
        {children}
      </Link>
    </TableCell>
  );
}

export default function PeoplePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);

  const filter: PersonType | undefined = location.pathname.includes("employees")
    ? "employee"
    : location.pathname.includes("freelancers")
      ? "freelancer"
      : undefined;

  const { data: people, isLoading } = usePeople(filter);
  const softDelete = useSoftDeletePerson();
  const { data: allSkillRatings } = useAllSkillRatings();
  const { data: defaultFunctions } = useDefaultFunctions();

  const title =
    filter === "employee"
      ? "Employees"
      : filter === "freelancer"
        ? "Freelancers"
        : "All People";

  const skillOptions = useMemo(
    () =>
      (defaultFunctions ?? []).map((fn) => ({ value: fn.id, label: fn.name })),
    [defaultFunctions],
  );

  const skillsByPerson = useMemo(() => {
    const map = new Map<
      string,
      Array<{ functionId: string; functionName: string; rating: number }>
    >();
    if (!allSkillRatings) return map;
    for (const sr of allSkillRatings) {
      const entry = {
        functionId: sr.default_function_id,
        functionName: sr.default_function.name,
        rating: sr.rating,
      };
      const existing = map.get(sr.person_id);
      if (existing) {
        existing.push(entry);
      } else {
        map.set(sr.person_id, [entry]);
      }
    }
    for (const skills of map.values()) {
      skills.sort(
        (a, b) =>
          b.rating - a.rating || a.functionName.localeCompare(b.functionName),
      );
    }
    return map;
  }, [allSkillRatings]);

  const filteredPeople = useMemo(() => {
    let result =
      people?.filter((p) => {
        const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
        return (
          fullName.includes(search.toLowerCase()) ||
          p.email?.toLowerCase().includes(search.toLowerCase())
        );
      }) ?? [];

    if (selectedSkillId) {
      result = result.filter((p) => {
        const skills = skillsByPerson.get(p.id);
        return skills?.some((s) => s.functionId === selectedSkillId);
      });

      result.sort((a, b) => {
        const aSkill = skillsByPerson
          .get(a.id)
          ?.find((s) => s.functionId === selectedSkillId);
        const bSkill = skillsByPerson
          .get(b.id)
          ?.find((s) => s.functionId === selectedSkillId);
        return (bSkill?.rating ?? 0) - (aSkill?.rating ?? 0);
      });
    }

    return result;
  }, [people, search, selectedSkillId, skillsByPerson]);

  const handleDelete = () => {
    if (deleteId) {
      softDelete.mutate(deleteId);
      setDeleteId(null);
    }
  };

  const getNewPath = () => {
    if (filter === "employee") return "/people/employees/new";
    if (filter === "freelancer") return "/people/freelancers/new";
    return "/people/new";
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader title={title}>
        <Button offlineEdit onClick={() => navigate(getNewPath())}>
          <Plus className="h-4 w-4" />
          Add Person
        </Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="w-full sm:w-80">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search people..."
          />
        </div>
        <div className="w-full sm:w-48">
          <EntityCombobox
            options={skillOptions}
            value={selectedSkillId}
            onChange={setSelectedSkillId}
            placeholder="Filter by skill"
            searchPlaceholder="Search skills..."
            emptyMessage="No skills found."
            allowClear
          />
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Skills</TableHead>
              {!filter && <TableHead>Type</TableHead>}
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={filter ? 5 : 6}
                  className="text-center py-8"
                >
                  Loading people...
                </TableCell>
              </TableRow>
            ) : filteredPeople.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={filter ? 5 : 6}
                  className="text-center py-8"
                >
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Users className="h-8 w-8" />
                    <p>
                      {search || selectedSkillId
                        ? "No people match your filters."
                        : "No people yet. Add your first one!"}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredPeople.map((person) => (
                <TableRow
                  key={person.id}
                  className="cursor-pointer hover:bg-muted/50"
                >
                  <PersonLinkCell
                    to={`/people/${person.id}`}
                    className="font-mono text-sm"
                  >
                    {person.number || "-"}
                  </PersonLinkCell>
                  <PersonLinkCell
                    to={`/people/${person.id}`}
                    className="font-medium"
                  >
                    {person.first_name} {person.last_name}
                  </PersonLinkCell>
                  <PersonLinkCell to={`/people/${person.id}`}>
                    {person.companies?.name || "-"}
                  </PersonLinkCell>
                  <PersonLinkCell to={`/people/${person.id}`}>
                    <div className="flex flex-wrap gap-1">
                      {(skillsByPerson.get(person.id) ?? []).map((skill) => (
                        <Badge
                          key={skill.functionId}
                          variant="secondary"
                          className="text-xs whitespace-nowrap"
                        >
                          {skill.functionName} ({skill.rating})
                        </Badge>
                      ))}
                    </div>
                  </PersonLinkCell>
                  {!filter && (
                    <PersonLinkCell to={`/people/${person.id}`}>
                      <PersonTypeBadge type={person.person_type} />
                    </PersonLinkCell>
                  )}
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        asChild
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button variant="ghost" size="icon-sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem offlineEdit asChild>
                          <Link to={`/people/${person.id}`}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          offlineEdit
                          className="text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(person.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Person?</AlertDialogTitle>
            <AlertDialogDescription>
              This will move the person to trash. You can restore it later if
              needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
